import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { 
  Briefcase, 
  Clock, 
  ClipboardCheck,
  AlertTriangle,
  FileText,
  ChevronRight,
  RefreshCw,
  Building2,
  Wrench,
  CheckCircle2
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function StatCard({ title, value, icon: Icon, color = "teal", onClick }) {
  const colors = {
    teal: "border-l-teal-500 text-teal-700",
    blue: "border-l-blue-500 text-blue-700",
    green: "border-l-green-500 text-green-700",
    red: "border-l-red-500 text-red-700",
    amber: "border-l-amber-500 text-amber-700",
    purple: "border-l-purple-500 text-purple-700",
  };

  return (
    <Card 
      className={`border-l-4 ${colors[color]} ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
      onClick={onClick}
    >
      <CardContent className="p-5">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm text-slate-500">{title}</div>
            <div className={`text-3xl font-bold mt-1 ${colors[color].split(' ')[1]}`}>{value}</div>
          </div>
          <Icon className={`h-8 w-8 opacity-30 ${colors[color].split(' ')[1]}`} />
        </div>
      </CardContent>
    </Card>
  );
}

export default function ClientPortalV2DashboardPage() {
  const [data, setData] = useState(null);
  const [recentJobs, setRecentJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const loadData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const [dashRes, jobsRes] = await Promise.all([
        axios.get(`${API}/api/system-expansion/client-v2/dashboard`, { headers }),
        axios.get(`${API}/api/system-expansion/client-v2/jobs?limit=5`, { headers }),
      ]);
      
      setData(dashRes.data);
      setRecentJobs(jobsRes.data.rows || []);
    } catch (err) {
      console.error("Failed to load dashboard:", err);
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  const stats = data?.stats || {};
  const brand = data?.brand || {};

  return (
    <div className="min-h-screen bg-slate-50" data-testid="client-v2-dashboard">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-700 to-teal-600 p-6 text-white">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-2xl font-bold" data-testid="brand-name">
            {brand.name || "Client Portal"}
          </h1>
          <p className="text-teal-100 text-sm mt-1">
            Simple maintenance visibility and control
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-6 space-y-6">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <StatCard 
            title="Total Jobs" 
            value={stats.total_jobs || 0} 
            icon={Briefcase}
            color="teal"
            onClick={() => navigate('/client-v2/jobs')}
          />
          <StatCard 
            title="Open Jobs" 
            value={stats.open_jobs || 0} 
            icon={Clock}
            color="blue"
            onClick={() => navigate('/client-v2/jobs')}
          />
          <StatCard 
            title="PPM Total" 
            value={stats.total_ppm || 0} 
            icon={ClipboardCheck}
            color="green"
            onClick={() => navigate('/client-v2/ppm')}
          />
          <StatCard 
            title="PPM Overdue" 
            value={stats.overdue_ppm || 0} 
            icon={AlertTriangle}
            color="red"
            onClick={() => navigate('/client-v2/ppm')}
          />
          <StatCard 
            title="Quotes Awaiting" 
            value={stats.quotes_awaiting || 0} 
            icon={FileText}
            color="amber"
            onClick={() => navigate('/client-v2/quotes')}
          />
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Button 
            onClick={() => navigate('/client-v2/jobs')}
            className="h-auto py-4 bg-white text-slate-700 border hover:bg-slate-50 justify-start"
            data-testid="view-jobs-btn"
          >
            <Briefcase className="h-5 w-5 mr-3 text-teal-600" />
            <div className="text-left">
              <div className="font-medium">View Jobs</div>
              <div className="text-xs text-slate-500">All maintenance jobs</div>
            </div>
          </Button>
          
          <Button 
            onClick={() => navigate('/client-v2/ppm')}
            className="h-auto py-4 bg-white text-slate-700 border hover:bg-slate-50 justify-start"
            data-testid="view-ppm-btn"
          >
            <ClipboardCheck className="h-5 w-5 mr-3 text-green-600" />
            <div className="text-left">
              <div className="font-medium">PPM Compliance</div>
              <div className="text-xs text-slate-500">Planned maintenance</div>
            </div>
          </Button>
          
          <Button 
            onClick={() => navigate('/client-v2/quotes')}
            className="h-auto py-4 bg-white text-slate-700 border hover:bg-slate-50 justify-start"
            data-testid="view-quotes-btn"
          >
            <FileText className="h-5 w-5 mr-3 text-amber-600" />
            <div className="text-left">
              <div className="font-medium">Quotes</div>
              <div className="text-xs text-slate-500">Review & approve</div>
            </div>
          </Button>
          
          <Button 
            onClick={() => navigate('/client-v2/jobs/new')}
            className="h-auto py-4 bg-teal-600 hover:bg-teal-700 text-white justify-start"
            data-testid="new-job-btn"
          >
            <Wrench className="h-5 w-5 mr-3" />
            <div className="text-left">
              <div className="font-medium">Report Issue</div>
              <div className="text-xs text-teal-100">Create new job</div>
            </div>
          </Button>
        </div>

        {/* Recent Jobs */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Clock className="h-4 w-4 text-teal-600" />
                Recent Jobs
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => navigate('/client-v2/jobs')}>
                View All <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {recentJobs.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <Briefcase className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No recent jobs</p>
              </div>
            ) : (
              <div className="space-y-3">
                {recentJobs.map((job) => (
                  <div 
                    key={job.job_id} 
                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50 transition-colors cursor-pointer"
                    onClick={() => navigate(`/client-v2/jobs/${job.job_id}`)}
                    data-testid={`job-item-${job.job_id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
                        <Wrench className="h-5 w-5 text-teal-600" />
                      </div>
                      <div>
                        <div className="font-medium text-sm">{job.job_reference}</div>
                        <div className="text-xs text-slate-500 truncate max-w-[200px]">
                          {job.description}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge className={
                        job.status === "Completed" ? "bg-green-100 text-green-700" :
                        job.status === "In Progress" ? "bg-blue-100 text-blue-700" :
                        "bg-slate-100 text-slate-700"
                      }>
                        {job.status}
                      </Badge>
                      <div className="text-xs text-slate-400 mt-1">
                        {job.park_name}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center text-xs text-slate-400 pt-4">
          Powered by LHS FM System
        </div>
      </div>
    </div>
  );
}
